import React, { useEffect, useState } from 'react';
/** Assets */
import { LogoSvg } from '../assets';

/** Components */
import { Card, CardContent, Typography, Box } from '@mui/material';
import { RequiredText } from '@components/forms/RequiredText';
import LoadingButton from '@mui/lab/LoadingButton';

/** Types, Utils */
import { useStyles } from './styles/ResetPass.styles';
import { useQuery } from '@hooks/useQuery';
import { validatePassword } from '@utils/validate';
import { authApi, UserUrls } from '@api/user';
import { ResetPassDialog } from '@components/dialogs/ResetPassDialog';

type Error = {
    error: boolean;
    message: string;
};

type Confirm = {
    password: Error;
    confirm: Error;
    submission: Error;
};

const initialErrorState: Confirm = {
    password: { error: false, message: '' },
    confirm: { error: false, message: '' },
    submission: { error: false, message: '' },
};

export const ResetPassword = () => {
    /** Static Hooks */
    const classes = useStyles();
    const query = useQuery();

    /** State */
    const [showDialog, setShowDialog] = useState(false);
    const [errorState, setErrorState] = useState<Confirm>(initialErrorState);
    const [state, setState] = useState({
        password: '',
        confirm: '',
    });

    /** State Hooks */
    useEffect(() => {
        setErrorState(
            state['password']
                ? {
                      ...errorState,
                      password: validatePassword(state['password']),
                  }
                : initialErrorState
        );
    }, [state.password]);

    useEffect(() => {
        setErrorState(
            state['confirm'] && state['password']
                ? {
                      ...errorState,
                      confirm: validatePassword(state['confirm'], state['password']),
                  }
                : initialErrorState
        );
    }, [state.confirm]);

    return (
        <div className={classes.base}>
            <div className={classes.root} title='Login'>
                <Card className={classes.card}>
                    <CardContent className={classes.content}>
                        <LogoSvg />

                        <Typography className={classes.title}>Enter New Password</Typography>

                        <RequiredText
                            stateKey={'password'}
                            label={'Password'}
                            state={state}
                            dispatch={setState}
                            disabled={false}
                            styles={useStyles}
                            type={'password'}
                            error={errorState['password'].error}
                            errorHelper={errorState['password'].message}
                        />

                        <RequiredText
                            stateKey={'confirm'}
                            label={'Confirm Password'}
                            state={state}
                            dispatch={setState}
                            disabled={!state['password'] || errorState['password'].error ? true : false}
                            styles={useStyles}
                            type={'password'}
                            error={errorState['confirm'].error}
                            errorHelper={errorState['confirm'].message}
                        />
                        {errorState.submission.error && <Box color='error.main'>{errorState.submission.message}</Box>}

                        <LoadingButton
                            loading={false}
                            disabled={state.password && state.confirm && !errorState.confirm.error ? false : true}
                            className={classes.submitButton}
                            size='large'
                            type='submit'
                            variant='contained'
                            onClick={() => {
                                const username = query.get('username');
                                const code = query.get('code');
                                /** Query String -> username, code, password: body */
                                if (!username || !code) {
                                    setErrorState({
                                        ...errorState,
                                        submission: { error: true, message: 'Invalid password reset url.' },
                                    });
                                } else {
                                    authApi
                                        .post(UserUrls.ConfirmPass, {
                                            username,
                                            code,
                                            password: state.password,
                                        })
                                        .then(({ status, data }) => {
                                            if (status === 200) {
                                                setShowDialog(true);
                                            } else {
                                                setErrorState({
                                                    ...errorState,
                                                    submission: { error: true, message: JSON.stringify(data) },
                                                });
                                            }
                                        });
                                }
                            }}>
                            Submit
                        </LoadingButton>
                    </CardContent>
                </Card>
            </div>

            <ResetPassDialog styles={useStyles} state={showDialog} dispatch={setShowDialog} />
        </div>
    );
};
